import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getJobs } from "@/api/jobs";
import { getReferrals } from "@/api/referrals";
import { getCompanies } from "@/api/companies";
import { getUser } from "@/api/users";
import { getUserStats, getRecruiterStats, getAdminStats } from "@/api/stats";
import { JobStatus } from "@/lib/schema";
import { auth } from "@/lib/firebase";

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<unknown> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const [path, ...args] = queryKey as [string, ...any[]];

    // Handle potential unauthorized - but since client-side, assume auth handled by Firebase
    try {
      switch (path) {
        case "/api/jobs":
          return await getJobs(args[0] || {});
        case "/api/jobs/available":
          return await getJobs({ status: JobStatus.ACTIVE });
        // Add case for /api/jobs/featured if needed, assume similar
        case "/api/referrals":
          return await getReferrals(args[0] || {});
        case "/api/companies":
          return await getCompanies();
        case "/api/users":
          if (args[1] === "stats") {
            return await getUserStats(args[0]);
          }
          return await getUser(args[0]);
        case "/api/stats/recruiter":
          return await getRecruiterStats(args[0] || auth.currentUser?.uid);
        case "/api/stats/admin":
          return await getAdminStats();
        // Add more cases as needed, e.g., for /api/rewards, /api/leaderboard
        default:
          throw new Error(`Unsupported API path: ${path}`);
      }
    } catch (error: unknown) {
      // Handle errors, e.g., if not authorized, based on unauthorizedBehavior
      if (unauthorizedBehavior === "returnNull" && (error as Error).message.includes("permission")) {
        return null;
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
